import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { MessageCircle, Phone, Sparkles, ShieldCheck, Star } from "lucide-react";
import { WA_LINK, PHONE_TEL, PHONE_DISPLAY, scrollToId } from "@/lib/contact";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), {
    stiffness: 120,
    damping: 18,
  });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden grain pt-[70px]"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-[radial-gradient(circle,rgba(108,190,69,0.16),transparent_65%)]" />
        <div className="absolute bottom-0 -left-52 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(130,203,126,0.18),transparent_65%)]" />
        <div className="absolute top-1/3 left-1/2 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.10),transparent_65%)]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-[#D4AF37]/50 bg-[#FDFBF2] px-4 py-2 mb-8"
            data-testid="hero-badge"
          >
            <Star className="w-3.5 h-3.5 text-[#D4AF37] fill-[#D4AF37]" />
            <span className="font-mono-accent text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#4A8F2A]">
              Est. 2023 · Angamaly – Manjapra, Kerala
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-[13.5vw] sm:text-7xl lg:text-[5.6rem]">
            <MaskedLine delay={0.45}>for Fresh</MaskedLine>
            <MaskedLine delay={0.58}>
              <span className="font-serif-accent italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#6CBE45] via-[#4A8F2A] to-[#5BA357]">
                and Dust free
              </span>
            </MaskedLine>
            <MaskedLine delay={0.71}>
              Home<span className="text-[#D4AF37]">.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg text-[#4A5A5E] leading-relaxed"
            data-testid="hero-subtext"
          >
            Professional deep-cleaning teams for homes, villas, flats and
            offices — serving Angamaly, Chalakudy, and surrounding regions.
            Trusted by premium homeowners and expatriates seeking impeccable
            standards.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="wa-pulse inline-flex items-center gap-3 rounded-full bg-[#25D366] text-white font-bold text-base px-8 py-4 hover:bg-[#1eb857] transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Book Now on WhatsApp
            </a>
            <a
              href={PHONE_TEL}
              data-testid="hero-call-cta"
              className="inline-flex items-center gap-2.5 rounded-full border-2 border-[#0F1A1C]/12 bg-white/70 backdrop-blur px-7 py-[14px] font-semibold text-[#0F1A1C] hover:border-[#6CBE45] hover:text-[#4A8F2A] transition-colors duration-300"
            >
              <Phone className="w-4.5 h-4.5 text-[#6CBE45]" />
              {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats"
          >
            {[
              ["500+", "Homes Transformed"],
              ["6", "Specialist Services"],
              ["100%", "Premium Home Care"],
            ].map(([num, label]) => (
              <div key={label} className="flex items-center gap-3">
                <span className="font-display text-2xl sm:text-3xl font-extrabold text-[#4A8F2A]">
                  {num}
                </span>
                <span className="text-xs font-mono-accent uppercase tracking-[0.16em] text-[#4A5A5E] leading-tight max-w-[110px]">
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.7, ease: EASE }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            onMouseMove={onTilt}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            className="relative"
            data-testid="hero-image-frame"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="gold-frame relative overflow-hidden rounded-[2rem] shadow-[0_40px_90px_-20px_rgba(74,143,42,0.4)]">
                <img
                  src="/assets/hero-house.jpg"
                  alt="Premium white villa with orange-tiled roof, spotless after PK Hygiene deep cleaning"
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4A8F2A]/25 via-transparent to-transparent" />
              </div>

              <div
                className="float-slow absolute -left-5 sm:-left-9 top-10 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-[0_16px_40px_-10px_rgba(15,26,28,0.25)] flex items-center gap-3"
                style={{ transform: "translateZ(50px)" }}
              >
                <span className="w-9 h-9 rounded-full bg-[#EFF8E8] flex items-center justify-center">
                  <Sparkles className="w-4.5 h-4.5 text-[#6CBE45]" />
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-bold">Spotless Finish</p>
                  <p className="text-[11px] text-[#4A5A5E]">Every corner, every time</p>
                </div>
              </div>

              <div
                className="float-slow absolute -right-4 sm:-right-8 bottom-12 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-[0_16px_40px_-10px_rgba(15,26,28,0.25)] flex items-center gap-3"
                style={{ transform: "translateZ(60px)", animationDelay: "1.4s" }}
              >
                <span className="w-9 h-9 rounded-full bg-[#FDFBF2] flex items-center justify-center">
                  <ShieldCheck className="w-4.5 h-4.5 text-[#D4AF37]" />
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-bold">Expat Home Care</p>
                  <p className="text-[11px] text-[#4A5A5E]">Photo & video proof shared</p>
                </div>
              </div>
            </motion.div>

            <div className="absolute -bottom-10 -left-10 w-28 h-28 hidden sm:block">
              <div className="spin-slow w-full h-full">
                <img
                  src="/assets/logo-round.png"
                  alt="PK Hygiene seal"
                  className="w-full h-full rounded-full shadow-[0_10px_30px_-8px_rgba(15,26,28,0.3)] ring-4 ring-white"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={(e) => scrollToId(e, "#about")}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-7 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[#4A5A5E] hover:text-[#4A8F2A] transition-colors"
        aria-label="Scroll to about section"
      >
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-[#6CBE45] to-transparent" />
      </motion.button>
    </section>
  );
}
